import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() =>
    (localStorage.getItem('aitools-theme') as 'light' | 'dark') || 'light'
  );

  const theme = createTheme({
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      button: { textTransform: 'none', fontWeight: 600 },
    },
    palette: {
      mode,
      primary: { main: mode === 'dark' ? '#FFB06A' : '#FF8428', contrastText: mode === 'dark' ? '#1B1511' : '#FFFFFF' },
      background: { default: mode === 'dark' ? '#171210' : '#F3E8DC', paper: mode === 'dark' ? '#241B17' : '#FFF8EF' },
      text: { primary: mode === 'dark' ? '#FFF8EF' : '#171615', secondary: mode === 'dark' ? '#D6C2AE' : '#71665D' },
      success: { main: '#10B981' },
      warning: { main: '#F59E0B' },
      error:   { main: '#EF4444' },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: { styleOverrides: { body: { backgroundColor: 'transparent' } } },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.07)',
            boxShadow: mode === 'dark'
              ? '0 4px 16px rgba(0,0,0,0.4)'
              : '0 4px 16px rgba(79,48,29,0.08)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          contained: { '&:hover': { transform: 'translateY(-1px)' } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
    },
  });

  const toggleTheme = () => {
    const next = mode === 'light' ? 'dark' : 'light';
    setMode(next);
    localStorage.setItem('aitools-theme', next);
  };

  useEffect(() => {
    document.body.dataset.theme = mode;
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, theme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
